import { Flex, Text } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { Banner, Checkbox, FormFeedback } from '@morressier/alembic';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { parseStringWithLinks } from 'src/utils/parseStringWithLinks';
import { IAuthorStepConfig } from 'src/hooks/useSubmissionStepsConfig';

const CoAuthorConsent = ({ configs }: { configs: IAuthorStepConfig }) => {
  const {
    formState: { errors },
    register
  } = useFormContext<ISubmissionFormData>();
  const { submissionContext } = useSubmissionContextData();
  const reviewAuthorInformation =
    submissionContext?.config.cms.authorSubmissionForm.reviewAuthorInformation;

  const bmmCoAuthorConsent =
    configs.CoAuthorManuscriptOriginalityConfirmed ||
    configs.CoAuthorManuscriptNotUnderConsiderationElsewhere ||
    configs.CoAuthorAuthorshipAndConflictDeclaration ||
    configs.CoAuthorResearchEthicsComplianceConfirmed ||
    configs.CoAuthorExportControlComplianceConfirmed;

  return (
    (configs.CoAuthorAgreeToSharing ||
      configs.CoAuthorsAgreeToEthics ||
      bmmCoAuthorConsent) && (
      <>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">
            {reviewAuthorInformation?.coAuthorConsent.mainLabel}
          </Text>
          <Text textStyle="textBody">
            {reviewAuthorInformation?.coAuthorConsent.secondaryLabel}
          </Text>
        </Flex>

        <Banner
          variant="attention"
          heading={
            reviewAuthorInformation?.potentialFundingRestrictions.mainLabel ??
            ''
          }
          content={
            reviewAuthorInformation?.potentialFundingRestrictions
              .secondaryLabel &&
            parseStringWithLinks(
              reviewAuthorInformation.potentialFundingRestrictions
                .secondaryLabel
            )
          }
        />

        {configs.CoAuthorsAgreeToEthics && (
          <Flex flexDirection="column" gap={2}>
            <Checkbox
              isInvalid={!!errors.metadata?.coauthorsAgreeToEthics}
              id={configs.CoAuthorsAgreeToEthics.caseDataFields.main}
              {...register(configs.CoAuthorsAgreeToEthics.caseDataFields.main)}
              ariaLabel={configs.CoAuthorsAgreeToEthics.params.labels.main}
            >
              {parseStringWithLinks(
                configs.CoAuthorsAgreeToEthics.params.labels.main
              )}
            </Checkbox>
            {errors.metadata?.coauthorsAgreeToEthics ? (
              <FormFeedback variant="error">
                All authors must consent to this policy to proceed
              </FormFeedback>
            ) : null}
          </Flex>
        )}

        {configs.CoAuthorAgreeToSharing && (
          <Flex flexDirection="column" gap={2}>
            <Checkbox
              isInvalid={!!errors.metadata?.coauthorsAgreeToSharing}
              id={configs.CoAuthorAgreeToSharing.caseDataFields.main}
              {...register(configs.CoAuthorAgreeToSharing.caseDataFields.main)}
              ariaLabel={configs.CoAuthorAgreeToSharing.params.labels.main}
            >
              {parseStringWithLinks(
                configs.CoAuthorAgreeToSharing.params.labels.main
              )}
            </Checkbox>

            {errors.metadata?.coauthorsAgreeToSharing ? (
              <FormFeedback variant="error">
                All authors must consent to this policy to proceed
              </FormFeedback>
            ) : null}
          </Flex>
        )}

        {configs.CoAuthorManuscriptOriginalityConfirmed && (
          <Flex flexDirection="column" gap={2}>
            <Checkbox
              isInvalid={
                !!errors.metadata?.coauthorManuscriptOriginalityConfirmed
              }
              id={
                configs.CoAuthorManuscriptOriginalityConfirmed.caseDataFields
                  .main
              }
              {...register(
                configs.CoAuthorManuscriptOriginalityConfirmed.caseDataFields
                  .main
              )}
              ariaLabel={
                configs.CoAuthorManuscriptOriginalityConfirmed.params.labels
                  .main
              }
            >
              {parseStringWithLinks(
                configs.CoAuthorManuscriptOriginalityConfirmed.params.labels
                  .main
              )}
            </Checkbox>

            {errors.metadata?.coauthorManuscriptOriginalityConfirmed ? (
              <FormFeedback variant="error">
                All authors must consent to this policy to proceed
              </FormFeedback>
            ) : null}
          </Flex>
        )}

        {configs.CoAuthorManuscriptNotUnderConsiderationElsewhere && (
          <Flex flexDirection="column" gap={2}>
            <Checkbox
              isInvalid={
                !!errors.metadata
                  ?.coauthorManuscriptNotUnderConsiderationElsewhere
              }
              id={
                configs.CoAuthorManuscriptNotUnderConsiderationElsewhere
                  .caseDataFields.main
              }
              {...register(
                configs.CoAuthorManuscriptNotUnderConsiderationElsewhere
                  .caseDataFields.main
              )}
              ariaLabel={
                configs.CoAuthorManuscriptNotUnderConsiderationElsewhere.params
                  .labels.main
              }
            >
              {parseStringWithLinks(
                configs.CoAuthorManuscriptNotUnderConsiderationElsewhere.params
                  .labels.main
              )}
            </Checkbox>

            {errors.metadata
              ?.coauthorManuscriptNotUnderConsiderationElsewhere ? (
              <FormFeedback variant="error">
                All authors must consent to this policy to proceed
              </FormFeedback>
            ) : null}
          </Flex>
        )}

        {configs.CoAuthorAuthorshipAndConflictDeclaration && (
          <Flex flexDirection="column" gap={2}>
            <Checkbox
              isInvalid={
                !!errors.metadata?.coauthorAuthorshipAndConflictDeclaration
              }
              id={
                configs.CoAuthorAuthorshipAndConflictDeclaration.caseDataFields
                  .main
              }
              {...register(
                configs.CoAuthorAuthorshipAndConflictDeclaration.caseDataFields
                  .main
              )}
              ariaLabel={
                configs.CoAuthorAuthorshipAndConflictDeclaration.params.labels
                  .main
              }
            >
              {parseStringWithLinks(
                configs.CoAuthorAuthorshipAndConflictDeclaration.params.labels
                  .main
              )}
            </Checkbox>

            {errors.metadata?.coauthorAuthorshipAndConflictDeclaration ? (
              <FormFeedback variant="error">
                All authors must consent to this policy to proceed
              </FormFeedback>
            ) : null}
          </Flex>
        )}

        {configs.CoAuthorResearchEthicsComplianceConfirmed && (
          <Flex flexDirection="column" gap={2}>
            <Checkbox
              isInvalid={
                !!errors.metadata?.coauthorResearchEthicsComplianceConfirmed
              }
              id={
                configs.CoAuthorResearchEthicsComplianceConfirmed.caseDataFields
                  .main
              }
              {...register(
                configs.CoAuthorResearchEthicsComplianceConfirmed.caseDataFields
                  .main
              )}
              ariaLabel={
                configs.CoAuthorResearchEthicsComplianceConfirmed.params.labels
                  .main
              }
            >
              {parseStringWithLinks(
                configs.CoAuthorResearchEthicsComplianceConfirmed.params.labels
                  .main
              )}
            </Checkbox>

            {errors.metadata?.coauthorResearchEthicsComplianceConfirmed ? (
              <FormFeedback variant="error">
                All authors must consent to this policy to proceed
              </FormFeedback>
            ) : null}
          </Flex>
        )}

        {configs.CoAuthorExportControlComplianceConfirmed && (
          <Flex flexDirection="column" gap={2}>
            <Checkbox
              isInvalid={
                !!errors.metadata?.coauthorExportControlComplianceConfirmed
              }
              id={
                configs.CoAuthorExportControlComplianceConfirmed.caseDataFields
                  .main
              }
              {...register(
                configs.CoAuthorExportControlComplianceConfirmed.caseDataFields
                  .main
              )}
              ariaLabel={
                configs.CoAuthorExportControlComplianceConfirmed.params.labels
                  .main
              }
            >
              {parseStringWithLinks(
                configs.CoAuthorExportControlComplianceConfirmed.params.labels
                  .main
              )}
            </Checkbox>

            {errors.metadata?.coauthorExportControlComplianceConfirmed ? (
              <FormFeedback variant="error">
                All authors must consent to this policy to proceed
              </FormFeedback>
            ) : null}
          </Flex>
        )}
      </>
    )
  );
};

export default CoAuthorConsent;
